/**
 * "Keep uploaded assets in the library" — the preference, and the one call that
 * acts on it.
 *
 * Uploading a local file into a snapshot embeds its bytes and leaves no trace
 * anywhere else, so the next snapshot that wants the same logo starts with
 * another trip to someone's Downloads folder. With this on, every local upload
 * is ALSO written to `customUploads/` in the scraped library and can be picked
 * from there afterwards.
 *
 * One preference for every upload path on purpose: the Assets tab's Add and
 * Replace, and Version Settings' per-dependency uploads all run through here, so
 * a single switch governs them rather than a checkbox repeated beside each
 * button (and forgotten on whichever one you happened to use).
 *
 * Module scope, not persisted — same reasoning as the library's remembered
 * folder: it should survive a dialog closing, not a reload.
 */

import { saveCustomAsset } from "./snapshotEditorApi";

let keepUploads = false;

export function getKeepUploads() {
  return keepUploads;
}

export function setKeepUploads(value) {
  keepUploads = value === true;
}

/** What the library can classify and browse — mirrors the server's list. */
const SAVEABLE_TYPES = new Set(["image", "audio", "video", "font", "bitmap-font", "atlas"]);

/** The key a pair type stores its metadata half under. */
const META_KEY = { "bitmap-font": "xml", atlas: "json" };

export function canKeepInLibrary(assetType) {
  return SAVEABLE_TYPES.has(assetType);
}

/**
 * Write an uploaded asset to the library, if the preference is on.
 *
 * Never throws and never blocks the upload: the bytes are already in the
 * snapshot by the time this runs, so a failure here costs a copy in the
 * library, not the user's work.
 *
 * @param {object} opts
 * @param {string} opts.baseID
 * @param {string} opts.assetType   image | video | font | bitmap-font
 * @param {string} opts.fileName    the local file's name, used for the stem
 * @param {*}      opts.source      data URL, or `{ image, xml }` for a bitmap font
 * @param {function} [opts.createSnackbar]
 * @returns {Promise<string|null>} the saved name, or null when nothing was saved
 */
export async function keepUploadInLibrary({
  baseID,
  assetType,
  fileName,
  source,
  createSnackbar,
}) {
  if (!keepUploads) return null;

  // With the switch ON, doing nothing quietly is the wrong answer — that is how
  // an upload "just didn't turn up" in the library with no clue why. Say which
  // part could not be done.
  if (!baseID) {
    createSnackbar?.("Uploaded, but not kept in the library (no base selected).", "warning", false);
    return null;
  }
  if (!canKeepInLibrary(assetType)) {
    createSnackbar?.(
      `Uploaded, but the library does not hold ${assetType || "this"} assets.`,
      "warning",
      false
    );
    return null;
  }

  const metaKey = META_KEY[assetType];
  const payload = metaKey
    ? { assetType, fileName, image: source?.image, [metaKey]: source?.[metaKey] }
    : { assetType, fileName, source };

  const isComplete = metaKey
    ? Boolean(payload.image && payload[metaKey])
    : typeof payload.source === "string";
  if (!isComplete) {
    createSnackbar?.("Uploaded, but the file could not be read back to keep it.", "warning", false);
    return null;
  }

  try {
    const result = await saveCustomAsset(baseID, payload);
    if (!result.ok) {
      createSnackbar?.(`Uploaded, but not kept in the library (${result.error}).`, "warning", false);
      return null;
    }
    const name = result.data?.name;
    createSnackbar?.(`Kept in the library as customUploads/${name}`, "success", false);
    return name;
  } catch (err) {
    createSnackbar?.(`Uploaded, but not kept in the library (${err.message}).`, "warning", false);
    return null;
  }
}
